import { Box, CircularProgress, List, Typography } from '@mui/material';
import BaseDialog from '@/components/ui/BaseDialog';
import { useAppContext } from '@/provider/contexts';
import { useRegistrationState } from '@/provider/registration/registrationContext';
import { deleteTagsAPI, deleteAPOrRTAPI } from '@/api/storeManagement/registration';
import { DeleteReportData, TAB_TYPE } from '@/types/pages/storeManagement/registration/registrationTypes';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRegistrationLifecycleState } from '@/provider/registration/registrationLifecycleContext';

const renderItem = (title: string, value: number | undefined) => {
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <Box sx={{ mr: 1, fontSize: '0.4rem' }}>●</Box>
            <Typography variant="body2">{title} : </Typography>
            <Typography variant="body1" sx={{ ml: 1, fontWeight: 'bold' }}>
                {value || 0}{' '}
            </Typography>
        </Box>
    );
};
interface DeleteReportContentProps extends DeleteReportData {
    currentTab: TAB_TYPE;
}

const DeleteReportContent = ({
    currentTab,
    validateMessage,
    temp_tags_removed,
    temp_tags_invalid,
    temp_tags_already_confirmed,
    removed_macs,
    invalid_macs,
    unprocessed_macs,
}: DeleteReportContentProps) => {
    const { t } = useTranslation('common');

    const deviceTypeLabel = useMemo(() => {
        switch (currentTab) {
            case TAB_TYPE.AP:
                return t('APs');
            case TAB_TYPE.RT:
                return t('Routers');
            case TAB_TYPE.TAG:
                return t('Tags');
            default:
                return t('None');
        }
    }, [currentTab, t]);
    return (
        <>
            <Typography variant="h6" gutterBottom sx={{ mb: 2.5 }}>
                {t('Delete {{type}} Report', { type: deviceTypeLabel })}
            </Typography>
            {validateMessage != '' && (
                <Typography variant="body2" gutterBottom>
                    {t('Deletion failed: {{msg}}', { msg: validateMessage })}
                </Typography>
            )}
            {currentTab === TAB_TYPE.TAG ? (
                <List>
                    {renderItem(t('Tags Removed'), temp_tags_removed?.length)}
                    {renderItem(t('Tags Invalid'), temp_tags_invalid?.length)}
                    {renderItem(t('Tags Already Confirmed'), temp_tags_already_confirmed?.length)}
                </List>
            ) : (
                <List>
                    {renderItem(t('Removed MACs'), removed_macs?.length)}
                    {renderItem(t('Invalid MACs'), invalid_macs?.length)}
                    {renderItem(t('Unprocessed MACs'), unprocessed_macs?.length)}
                </List>
            )}
        </>
    );
};

const DeleteDialog = () => {
    const { t } = useTranslation('common');
    const { manageStoreId, setNotification } = useAppContext();
    const { state, dispatch } = useRegistrationState();
    const { refreshRegistrationNow } = useRegistrationLifecycleState();
    const [isAfterDelete, setIsAfterDelete] = useState(false);
    const [isDeleteLoading, setIsDeleteLoading] = useState(false);
    const [deleteReportData, setDeleteReportData] = useState<DeleteReportData | null>(null);

    // Calculation
    const contentWords = useMemo(() => {
        const count = state.deviceInfoState.selectedInfraDevices.length;
        return t('deleteConfirm', { count });
    }, [state.deviceInfoState.selectedInfraDevices, t]);

    // Events
    const handleOnClose = () => {
        dispatch({ type: 'TOGGLE_DIALOG', payload: { dialog: 'isDeleteOpen', isOpen: false } });
        setTimeout(() => {
            setDeleteReportData(null);
            setIsAfterDelete(false);
        }, 300);
    };
    const handleConfirmedHandler = async () => {
        setIsAfterDelete(true);
        setIsDeleteLoading(true);
        const postData = {
            mac_list: state.deviceInfoState.selectedInfraDevices.map((item) => item.mac),
            store_id: manageStoreId as number,
        };
        let responseData;
        if (state.currentTab === TAB_TYPE.TAG) {
            const { data } = await deleteTagsAPI(postData);
            responseData = data;
        } else {
            const { data } = await deleteAPOrRTAPI(postData);
            responseData = data;
        }
        if (responseData?.isSuccess) {
            setNotification({ message: responseData.message, severity: 'success' });
            void refreshRegistrationNow();
        } else {
            setNotification({ message: responseData.message, severity: 'error' });
        }
        setDeleteReportData({
            ...responseData.data,
            validateMessage: responseData.message,
        });
        setIsDeleteLoading(false);
    };
    return (
        <>
            <BaseDialog
                dialogTitle={t('Delete Devices')}
                open={state.dialogState.isDeleteOpen}
                confirmButtonText={isAfterDelete ? undefined : t('Confirm')}
                cancelButtonText={isAfterDelete ? t('Cancel') : t('Close')}
                onConfirm={handleConfirmedHandler}
                onClose={handleOnClose}
            >
                <Box sx={{ px: 2 }}>
                    {isAfterDelete ? (
                        isDeleteLoading ? (
                            <Box>
                                <CircularProgress />
                            </Box>
                        ) : (
                            <DeleteReportContent
                                {...deleteReportData}
                                currentTab={state.currentTab}
                                validateMessage={deleteReportData?.validateMessage}
                            />
                        )
                    ) : (
                        <Typography>{contentWords}</Typography>
                    )}
                </Box>
            </BaseDialog>
        </>
    );
};
export default DeleteDialog;
